import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { CustomCard } from '../components/CustomCard';
import { ToastContainer, toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import Spinner from '../components/Spinner';

const Dashboard = () => {
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const [recentPackages, setRecentPackages] = useState([]);
  const [packageCount, setPackageCount] = useState(0);
  const [userPackageCount, setUserPackageCount] = useState(0);
  const [deliveryService, setDeliveryService] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  const fetchData = async () => {
    const url = user.role === "Courier"
      ? `${apiUrl}/api/packages/recent/${user.courier_id}`
      : `${apiUrl}/api/packages/recent`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      setRecentPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPackageCount = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/packages/approved-today`);
      const data = await response.json();
      setPackageCount(data.count || 0);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUserPackageCount = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/packages/approved-today?id=${user.id}`);
      const data = await response.json();
      setUserPackageCount(data.count || 0);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDataByEndpoint = async (endpoint, setState) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}${endpoint}`);
      if (!response.ok) throw new Error(`Failed to fetch data from ${endpoint}`);
      const data = await response.json();
      setState(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPackageCount();
    fetchUserPackageCount();
    fetchDataByEndpoint('/api/delivery-service', setDeliveryService);
    fetchDataByEndpoint('/api/shipments/all', setShipments);
    fetchDataByEndpoint('/api/courier/all', setCouriers);
    fetchDataByEndpoint('/api/accounts/all', setAccounts);
  }, []);

  const columns = [
    { name: 'Tracking Number', selector: row => row.tracking_number, sortable: true },
    { name: 'Delivery Service', selector: row => deliveryService.find(ds => ds.id === row.delivery_service_id)?.name || 'N/A', sortable: true },
    { name: 'Shipper', selector: row => couriers.find(cr => cr.id === row.shipper_id)?.username || 'N/A', sortable: true },
    { name: 'Account', selector: row => accounts.find(ac => ac.id === row.account_id)?.username || 'N/A', sortable: true },
    { name: 'Shipment', selector: row => shipments.find(sh => sh.id === row.shipment_id)?.shipment_number || 'N/A', sortable: true },
    { name: 'Weight (lbs)', selector: row => row.weight || 'N/A', sortable: true },
    { name: 'Description', selector: row => row.description || 'N/A' },
    { name: 'Instructions', selector: row => row.instructions || 'N/A' },
    { name: 'Package Value', selector: row => row.package_value || 'N/A' },
    { name: 'Consignee', selector: row => row.consignee || 'N/A' }
  ];

  return (
    <Layout>
      <div className="w-full max-w-screen-xl mx-auto px-4 py-6">
        <h2 className="text-3xl font-bold mb-4">Dashboard</h2>

        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6 w-[100%]">
            <div className="bg-white shadow-md rounded-md p-6 overflow-hidden">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                <h3 className="text-2xl font-semibold">Recent Packages</h3>

                <div className="flex flex-wrap gap-4">
                  {user.role === "super-admin" && (
                    <CustomCard title="Packages Processed Today" value={packageCount} />
                  )}
                  {user.role !== "Courier" && (
                    <CustomCard title="Packages Processed By You" value={userPackageCount} />
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <DataTable
                  columns={columns}
                  data={recentPackages}
                  pagination
                  highlightOnHover
                  striped
                  responsive
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </Layout>
  );
};

export default Dashboard;
